import * as go from 'gojs';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { isDevelopment, isTesting } from 'vet-bones/utils';

import { transactionsToSkipProjectDiagramUpdate } from 'src/containers/ProjectDiagram/constants/transactions';
import {
  hideContextMenu,
  showContextMenu,
} from 'src/containers/ProjectDiagram/handlers/contextMenu';
import { diagramChangedSelection } from 'src/containers/ProjectDiagram/handlers/diagram';
import {
  hideToolTip,
  showTooltip,
} from 'src/containers/ProjectDiagram/handlers/tooltip';
import { useDiagramObjectClick } from 'src/containers/ProjectDiagram/hooks/useDiagramObjectClick';
import { useDiagramObjectDoubleClick } from 'src/containers/ProjectDiagram/hooks/useDiagramObjectDoubleClick';
import { createDiagram } from 'src/containers/ProjectDiagram/templates/diagram';
import { ExtendedDiagram } from 'src/containers/ProjectDiagram/templates/extensions/diagram';
import { ContextMenuData } from 'src/containers/ProjectDiagram/types/contextMenu';
import {
  Diagram,
  DiagramLink,
  DiagramSnapshot,
} from 'src/containers/ProjectDiagram/types/diagram';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import { TooltipData } from 'src/containers/ProjectDiagram/types/tooltip';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
  commitDiagramWhileSkippingUndoManager,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { fixUnconnectedLinkPoints } from 'src/containers/ProjectDiagram/utils/fixUnconnectedLinkPoints';
import { rescaleDiagram } from 'src/containers/ProjectDiagram/utils/rescaleDiagram';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';
import { setDiagramHighlight } from 'src/containers/ProjectDiagram/utils/setDiagramHighlight';
import { setDiagramSelection } from 'src/containers/ProjectDiagram/utils/setDiagramSelection';
import { transformModelToSnapshot } from 'src/containers/ProjectDiagram/utils/transformModelToSnapshot';

const isDev = isDevelopment();
const isTest = isTesting();

const INITIAL_LAYOUT_TRANSACTION_NAME = 'CommitedInitialLayoutTransaction';

export const DiagramContext = React.createContext<Diagram | undefined>(
  undefined
);

export const useDiagram = () => {
  return React.useContext(DiagramContext);
};

type UseDiagramValueProps = {
  diagramSnapshot: DiagramSnapshot | null;
  handleDiagramUpdate: (snapshot: DiagramSnapshot) => void;
  handleDiagramSelection: (selection: go.Set<go.Part>) => void;
  setContextMenu: (_: ContextMenuData | null) => void;
  setTooltip: (data: TooltipData | null) => void;
};

export const useDiagramValue = ({
  diagramSnapshot,
  handleDiagramUpdate,
  handleDiagramSelection,
  setContextMenu,
  setTooltip,
}: UseDiagramValueProps) => {
  const processedIds = React.useRef<Set<string>>(new Set());
  const [diagramValue, setDiagramValue] = React.useState<Diagram | undefined>(
    undefined
  );

  const handleClick = useDiagramObjectClick();
  const handleDoubleClick = useDiagramObjectDoubleClick();

  let currentDiagram = diagramValue;

  const diagramSnapshotId = diagramSnapshot?.id;
  if (diagramSnapshotId && !processedIds.current.has(diagramSnapshotId)) {
    processedIds.current.clear();
    processedIds.current.add(diagramSnapshotId);

    const diagram = createDiagram({
      modelSnapshot: diagramSnapshot?.model,
      showContextMenu: showContextMenu(
        document.createElement('div'),
        setContextMenu
      ),
      hideContextMenu: hideContextMenu(setContextMenu),
      showToolTip: showTooltip(setTooltip),
      hideToolTip: hideToolTip(setTooltip),
      diagramChangedSelection: diagramChangedSelection((e) =>
        handleDiagramSelection(e.diagram.selection)
      ),
      handleClick,
      handleDoubleClick,
    });

    diagram.addDiagramListener('InitialLayoutCompleted', (e) => {
      if (
        e.diagram.layout.isInitial ||
        e.diagram.model.modelData.flaggedForRedraw
      ) {
        setDiagramSelection()(e.diagram as ExtendedDiagram);
        const hasExistingViewport = e.diagram.model.modelData.viewportBounds;
        if (!hasExistingViewport) {
          requestAnimationFrame(() => {
            commitDiagramWhileSkippingUndoManager(
              diagram,
              (_, diagram) => rescaleDiagram(diagram),
              INITIAL_LAYOUT_TRANSACTION_NAME
            );
          });
        }

        commitDiagramModel(
          e.diagram as Diagram,
          (model, diagram) => {
            model.linkDataArray.forEach((linkData) => {
              fixUnconnectedLinkPoints(linkData)(model, diagram);
            });

            setDataProperty<ModelData>(model)(
              model.modelData,
              'flaggedForRedraw',
              false
            );
          },
          {
            skipsCheck: true,
            layout: DiagramModelCommitLayout.FULL,
            tname: INITIAL_LAYOUT_TRANSACTION_NAME,
          }
        );

        // Force the diagram to update and redraw after invalidating routes
        // This ensures links are actually rendered.
        requestAnimationFrame(() => {
          commitDiagramWhileSkippingUndoManager(
            e.diagram as Diagram,
            (_model, diagram) => {
              // Force all links to invalidate routes again within the transaction
              diagram.links.each((link) => {
                const diagramLink = link as DiagramLink;
                diagramLink.invalidateRoute();
              });
            }
          );

          e.diagram.updateAllTargetBindings();
        });
      }
    });

    diagram.addModelChangedListener((e) => {
      if (
        e.model &&
        e.isTransactionFinished &&
        (e.propertyName === 'FinishedUndo' || e.propertyName === 'FinishedRedo')
      ) {
        diagram.commit(setDiagramHighlight());
        // undo/redo will restore diagram to previous viewstate while sometimes
        // ignoring current bindings (ex: fading with selection)
        diagram.updateAllTargetBindings('selection');
      }
      if (
        e.model &&
        e.isTransactionFinished &&
        e.oldValue !== 'Initial Layout' &&
        e.oldValue !== INITIAL_LAYOUT_TRANSACTION_NAME &&
        !transactionsToSkipProjectDiagramUpdate.has(e.oldValue)
      ) {
        const id = uuid();
        processedIds.current.add(id);
        handleDiagramUpdate({ id, model: transformModelToSnapshot(e.model) });
      }
      if (
        e.model?.modelData.hiddenModelIds.length > 0 &&
        e.oldValue === 'Initial Layout'
      ) {
        diagram.updateAllTargetBindings('visible');
      }
    });

    if (isDev || isTest) {
      window.diagram = diagram;
    }

    currentDiagram = diagram;
    setDiagramValue(diagram);
  }

  return currentDiagram;
};
